import {Button, Grid, Typography} from '@yandex-delivery/blocks'
import React, {useCallback, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {fetchEditOptions, fetchSharedRouteEditAuth, fetchSharedRouteInfo} from 'src/api/CargoC2CAPI'
import type {BaseWindowSettings, ContentListItem, ExtendDeliveryExpirationDate} from 'src/api/types'
import {tagToUrl} from 'src/shared/utils'

import {useSharedRoute} from '../../hooks'
import {classes} from './ExtendExpirationButton.classes'
import {ExtendExpirationModal} from './ExtendExpirationModal'

type Props = {
  action: ExtendDeliveryExpirationDate
  leadIcon?: ContentListItem['lead_icon']
}

export const ExtendExpirationButton: React.FC<Props> = ({action, leadIcon}) => {
  const [confirmationWindow, setConfirmationWindow] = useState<BaseWindowSettings | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const location = useLocation()
  const sharingKey = location.hash.substring(1)
  const [, startRouteTransition] = useSharedRoute()

  const handleConfirm = useCallback(
    async (date?: string) => {
      const dateToUse = date || selectedDate
      if (!dateToUse || !sharingKey) return

      setConfirmationWindow(null)

      try {
        await fetchSharedRouteEditAuth(sharingKey, dateToUse)
        startRouteTransition(() => fetchSharedRouteInfo({key: sharingKey}))
      } catch (error) {
        console.error('[ExtendExpirationButton] Edit failed:', error)
      } finally {
        setIsProcessing(false)
      }
    },
    [sharingKey, selectedDate, startRouteTransition],
  )

  const handlePress = useCallback(async () => {
    if (isProcessing || !sharingKey) return

    setIsProcessing(true)

    try {
      const requestId = crypto.randomUUID()
      const optionsResponse = await fetchEditOptions(sharingKey, requestId)

      const options = optionsResponse.delivery_options?.options
      if (!options || options.length === 0) {
        console.error('[ExtendExpirationButton] No storage period options available')
        setIsProcessing(false)
        return
      }

      const date = options[0]?.expiration_date
      if (!date) {
        console.error('[ExtendExpirationButton] No expiration date in first option')
        setIsProcessing(false)
        return
      }

      setSelectedDate(date)

      // Show confirmation modal if provided by backend
      if (optionsResponse.action?.main_window) {
        setConfirmationWindow(optionsResponse.action.main_window)
        // Keep isProcessing=true while modal is shown
      } else {
        // If no modal - send request immediately
        await handleConfirm(date)
      }
    } catch (error) {
      console.error('[ExtendExpirationButton] Failed to fetch edit options:', error)
      setIsProcessing(false)
    }
  }, [sharingKey, isProcessing, handleConfirm])

  const handleCloseModal = useCallback(() => {
    setConfirmationWindow(null)
    setSelectedDate(null)
    setIsProcessing(false)
  }, [])

  return (
    <>
      <Grid alignItems="center" className={classes.root} container direction="row" spacing={1}>
        {leadIcon && (
          <Grid item>
            <Grid alignItems="center" container item>
              <img
                alt=""
                className={classes.image}
                data-theme-invert
                loading="lazy"
                src={tagToUrl(leadIcon.image_tag)}
              />
            </Grid>
          </Grid>
        )}
        <Grid alignItems="center" container item justify="space-between" wrap="nowrap" xs>
          <Grid item>
            <Typography variant="body2">{action.badge_text}</Typography>
          </Grid>
          <Grid item>
            <Button
              className={classes.pill}
              disabled={isProcessing}
              onPress={handlePress}
              shape="circle"
              size="s"
              variant="ghost"
            >
              <Typography className={classes.pillText} thickness="medium" variant="caption1">
                {action.title}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {confirmationWindow && (
        <ExtendExpirationModal
          onClose={handleCloseModal}
          onConfirm={() => handleConfirm()}
          opened={!!confirmationWindow}
          window={confirmationWindow}
        />
      )}
    </>
  )
}
